import { useState } from "react";
import { X, CaretDown, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AiBuddy from "@/components/AiBuddy";

// The one Custom-commands dialog. Jun 2026 (Randy): the YTRE "Mold transcript"
// menu opens THIS, not a lookalike — "if you make a new operation here, or
// there its the same". Workbench mounts it too, so there is a single place
// where an operation is written.
export default function CustomCommandsModal({
  siloId = null,
  scope = "slots",
  commands = [],
  onReload,
  onCreated,
  onClose,
  requestConfirm,
  showOutputSize = true,
  zClass = "z-50",
}) {
  const [editingId, setEditingId] = useState(null);
  const [draftCmd, setDraftCmd] = useState({ name: "", instructions: "", default_output_pct: null });
  const [savingCmd, setSavingCmd] = useState(false);

  const custom = commands.filter((c) => !c.is_global);
  const blankDraft = () => setDraftCmd({ name: "", instructions: "", default_output_pct: null });
  const openEdit = (cmd) => {
    setEditingId(cmd.id);
    setDraftCmd({ name: cmd.name, instructions: cmd.instructions, default_output_pct: cmd.default_output_pct ?? null });
  };
  const cancelEdit = () => { setEditingId(null); blankDraft(); };

  const saveDraft = async () => {
    if (!draftCmd.name.trim() || !draftCmd.instructions.trim()) return toast.error("Name and instructions required.");
    setSavingCmd(true);
    try {
      if (editingId) {
        const r = await api.patch(`/commands/${editingId}`, draftCmd);
        toast.success(`Command "${r.data.name}" updated.`);
      } else {
        const r = await api.post("/commands", { ...draftCmd, scope });
        toast.success(`Command "${r.data.name}" saved.`);
        onCreated && onCreated(r.data);
      }
      setEditingId(null); blankDraft();
      onReload && (await onReload());
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save command"); }
    finally { setSavingCmd(false); }
  };

  const deleteCustom = (cmd) => {
    if (cmd.is_global) return;
    const run = async () => {
      try {
        await api.delete(`/commands/${cmd.id}`);
        onReload && (await onReload());
        if (editingId === cmd.id) cancelEdit();
        toast.success("Deleted.");
      } catch { toast.error("Delete failed"); }
    };
    if (requestConfirm) {
      requestConfirm({ title: `Delete "${cmd.name}"?`, message: "This cannot be undone.", danger: true, confirmLabel: "Delete", onConfirm: run });
      return;
    }
    if (window.confirm(`Delete "${cmd.name}"? This cannot be undone.`)) run();
  };

  return (
    <div className={`fixed inset-0 ${zClass} flex items-center justify-center p-4`} data-testid="customize-dialog">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="modal-safe relative z-10 w-full max-w-xl bg-white border hairline rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b hairline">
          <div className="font-serif-display text-lg">Custom commands</div>
          <button onClick={onClose} data-testid="customize-close" className="p-2 rounded-md text-[#6B6A68] hover:bg-[#F0F1EE]"><X size={18} /></button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-5">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-2">Your custom commands</div>
            {custom.length === 0 ? (
              <div className="text-sm text-[#6B6A68]">No custom commands yet. Create one below.</div>
            ) : (
              <ul className="divide-y hairline border hairline rounded-md">
                {custom.map((c) => (
                  <li key={c.id} className={`p-3 flex items-start gap-3 ${editingId === c.id ? "bg-[#F0F1EE]" : ""}`} data-testid={`cmd-row-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1C1B1A]">{c.name}</div>
                      <div className="text-xs text-[#6B6A68] mt-1 line-clamp-2">{c.instructions}</div>
                    </div>
                    <button onClick={() => openEdit(c)} data-testid={`cmd-edit-${c.id}`}
                      className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-[#FDFCF8]"><PencilSimple size={14} weight="duotone" /></button>
                    <button onClick={() => deleteCustom(c)} data-testid={`cmd-delete-${c.id}`}
                      className="p-1.5 rounded-md text-[#6B6A68] hover:text-[#C16E5A] hover:bg-[#FDFCF8]"><Trash size={14} weight="duotone" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t hairline pt-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68]">{editingId ? "Edit command" : "Create new"}</div>
              {editingId && <button onClick={cancelEdit} className="text-xs text-[#6B6A68] hover:text-[#1C1B1A]">Cancel edit</button>}
            </div>
            <label className="block text-sm text-[#6B6A68] mt-1 mb-1.5">Name</label>
            <input data-testid="cmd-draft-name" value={draftCmd.name} onChange={(e) => setDraftCmd({ ...draftCmd, name: e.target.value })}
              className="w-full bg-white border hairline rounded-md px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            <label className="block text-sm text-[#6B6A68] mt-3 mb-1.5">Instructions</label>
            <textarea data-testid="cmd-draft-instructions" rows={5} value={draftCmd.instructions}
              onChange={(e) => setDraftCmd({ ...draftCmd, instructions: e.target.value })}
              className="w-full bg-white border hairline rounded-md px-4 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]" />
            {showOutputSize && (
              <>
                <label className="block text-sm text-[#6B6A68] mt-3 mb-1.5">Default output size</label>
                <div className="relative">
                  <select
                    data-testid="cmd-draft-output-pct"
                    value={draftCmd.default_output_pct == null ? "" : String(draftCmd.default_output_pct)}
                    onChange={(e) => setDraftCmd({ ...draftCmd, default_output_pct: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                    className="appearance-none w-full bg-white border hairline rounded-md px-4 py-2.5 pr-9 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]">
                    <option value="">Auto · model decides (best for analysis / grammar)</option>
                    {[10, 15, 20, 25, 30, 40, 50, 60, 75, 90, 100].map((p) => (
                      <option key={p} value={p}>~{p}% of the input</option>
                    ))}
                  </select>
                  <CaretDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A68] pointer-events-none" />
                </div>
                <p className="text-[11px] text-[#9A9892] mt-1">Pre-sets the Workbench output-size slider when this command is picked. Auto = no size hint (it won't shrink a grammar pass).</p>
              </>
            )}
            <div className="mt-3 flex justify-end">
              <button onClick={saveDraft} disabled={savingCmd} data-testid="cmd-draft-save"
                className="px-5 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] text-sm disabled:opacity-60">
                {savingCmd ? "Saving…" : (editingId ? "Save changes" : "Save command")}
              </button>
            </div>
          </div>

          {/* AI Command Assist — pastes drafts straight into Instructions */}
          <div className="border-t hairline pt-5" data-testid="ai-buddy-mount-commands">
            <AiBuddy
              surface="commands-editor"
              siloId={siloId}
              compact
              title="AI Command Assist"
              subtitle="Command-drafting helper"
              emptyHint="Tell Command Assist your intention — have it make a short, accurate command to achieve it!"
              onPasteToInstructions={(text) => {
                setDraftCmd((d) => ({ ...d, instructions: text }));
                toast.success("Pasted into Instructions field.");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
